import TaskEventReplay from './TaskEventReplay'
import Spinner from './Spinner'
import useIsMobile from '../hooks/useIsMobile'
import { explorationHost } from '../lib/initialTestRun'
import { taskDetailUrl } from '../lib/taskDetailPath'
import './InitialTestRunView.css'

export interface InitialTestRunViewProps {
  projectId: string
  targetUrl: string | null
  projectName: string | null
  explorationId: string | null
  currentTask: { title: string; description: string } | null
  journeyCount: number
  explorationCount: number
}

export default function InitialTestRunView({
  projectId,
  targetUrl,
  projectName,
  explorationId,
  currentTask,
  journeyCount,
  explorationCount,
}: InitialTestRunViewProps) {
  const host = explorationHost(targetUrl, projectName)
  // Desktop fits the whole view (stepper + replay + footer) into the viewport with the replay flexing
  // to fill the leftover height. Mobile keeps natural flow (the page scrolls) and the replay uses its
  // default width-derived height.
  const isMobile = useIsMobile()

  return (
    <div className="initial-test-run" data-testid="initial-test-run">
      <header className="initial-test-run__header">
        <h1 className="initial-test-run__title">Exploring {host}…</h1>
        <p className="initial-test-run__subtitle">
          Agents are exploring your app live. You can leave this page and we'll notify you when done.
        </p>
      </header>

      <div className="initial-test-run__replay" data-testid="initial-test-run-replay">
        {explorationId ? (
          <TaskEventReplay
            source={{ explorationId }}
            taskHref={taskDetailUrl(projectId, explorationId)}
            active
            fitHeight={!isMobile}
          />
        ) : (
          <div className="initial-test-run__replay-placeholder" role="status">
            Waiting for exploration to start…
          </div>
        )}
      </div>

      <footer className="initial-test-run__footer" data-testid="initial-test-run-footer">
        <div className="initial-test-run__task">
          <Spinner size="md" className="initial-test-run__task-spinner" label="Task in progress" />
          <div className="initial-test-run__task-text">
            <div className="initial-test-run__task-title">
              {currentTask?.title ?? 'Starting initial test run…'}
            </div>
          </div>
        </div>
        <div className="initial-test-run__counts" aria-label="Discovery counts">
          <span>{journeyCount} journey{journeyCount === 1 ? '' : 's'}</span>
          <span className="initial-test-run__counts-divider" aria-hidden />
          <span>{explorationCount} exploration{explorationCount === 1 ? '' : 's'}</span>
        </div>
      </footer>
    </div>
  )
}
